import {
    type AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DOCUMENT,
    inject,
    Input,
    type OnDestroy,
    type TemplateRef,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, type FormControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { Store } from '@ngrx/store';
import type { Classes } from 'jss';

import { WebBaseControlTypedSettingsComponent } from '@web/widgets/common/abstract/web-base-control-typed-settings.component';
import {
    type MiniCartSettingsFormGroupType,
    type MiniCartSettingsFormType,
    type ShopCartWidgetData,
} from '@web/widgets/shop-cart/type/shop-cart-widget.type';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { previewPortalAttachAction } from '@web/store/page-structure/widgets/widgets.actions';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';

import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';
import { ShopCartMiniCartPreviewComponent } from './shop-cart-mini-cart-preview/shop-cart-mini-cart-preview.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'web-shop-cart-mini-cart-settings',
    templateUrl: './shop-cart-mini-cart-settings.component.html',
    styleUrls: ['./shop-cart-mini-cart-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        WebSettingsModule,
        FormsModule,
        ReactiveFormsModule,
        WebColorPickerComponent,
        SpInputNumComponent,
        TranslateModule,
        ShopCartMiniCartPreviewComponent,
        AsyncPipe,
    ],
})
export class ShopCartMiniCartSettingsComponent
    extends WebBaseControlTypedSettingsComponent<MiniCartSettingsFormGroupType>
    implements AfterViewInit, OnDestroy
{
    @Input() public data: ShopCartWidgetData;
    @Input() public classes: Classes;

    @ViewChild('modalPreviewPortal') public modalPreviewPortal: TemplateRef<unknown>;

    public readonly BORDER_RADIUS_MIN: number = 0;
    public readonly BORDER_RADIUS_MAX: number = 250;

    private readonly store$: Store = inject(Store);
    private readonly document: Document = inject(DOCUMENT);
    private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
    private readonly settingsFacade: SettingsFacade = inject(SettingsFacade);
    private portalHost: DomPortalOutlet;

    public readonly shopSettings$ = this.settingsFacade.shopSettings$;

    public ngAfterViewInit(): void {
        const portalHTMLElement = this.document.querySelector('#modalPreviewContent');
        if (!portalHTMLElement) return;

        this.portalHost = new DomPortalOutlet(portalHTMLElement);
        this.portalHost.attach(new TemplatePortal(this.modalPreviewPortal, this.viewContainerRef));
        this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: true }));
    }

    public override ngOnDestroy(): void {
        this.portalHost?.detach();
        this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: false }));
        super.ngOnDestroy();
    }

    public get modalBackgroundColor(): MiniCartSettingsFormType['modalBackgroundColor'] {
        return this.itemForm.get('modalBackgroundColor') as MiniCartSettingsFormType['modalBackgroundColor'];
    }

    public get modalBorderRadius(): MiniCartSettingsFormType['modalBorderRadius'] {
        return this.itemForm.get('modalBorderRadius') as MiniCartSettingsFormType['modalBorderRadius'];
    }

    public get separatorColor(): MiniCartSettingsFormType['separatorColor'] {
        return this.itemForm.get('separatorColor') as MiniCartSettingsFormType['separatorColor'];
    }

    public get textColor(): MiniCartSettingsFormType['textColor'] {
        return this.itemForm.get('textColor') as MiniCartSettingsFormType['textColor'];
    }

    public get priceColor(): MiniCartSettingsFormType['priceColor'] {
        return this.itemForm.get('priceColor') as MiniCartSettingsFormType['priceColor'];
    }

    public get oldPriceColor(): MiniCartSettingsFormType['oldPriceColor'] {
        return this.itemForm.get('oldPriceColor') as MiniCartSettingsFormType['oldPriceColor'];
    }

    public get deleteButtonIconColor(): MiniCartSettingsFormType['deleteButtonIconColor'] {
        return this.itemForm.get('deleteButtonIconColor') as MiniCartSettingsFormType['deleteButtonIconColor'];
    }

    public get deleteButtonBgColor(): MiniCartSettingsFormType['deleteButtonBgColor'] {
        return this.itemForm.get('deleteButtonBgColor') as MiniCartSettingsFormType['deleteButtonBgColor'];
    }

    public get countButtonTextColor(): MiniCartSettingsFormType['countButtonTextColor'] {
        return this.itemForm.get('countButtonTextColor') as MiniCartSettingsFormType['countButtonTextColor'];
    }

    public get countButtonBgColor(): MiniCartSettingsFormType['countButtonBgColor'] {
        return this.itemForm.get('countButtonBgColor') as MiniCartSettingsFormType['countButtonBgColor'];
    }

    public get countButtonBorderColor(): MiniCartSettingsFormType['countButtonBorderColor'] {
        return this.itemForm.get('countButtonBorderColor') as MiniCartSettingsFormType['countButtonBorderColor'];
    }

    public get countButtonRounding(): MiniCartSettingsFormType['countButtonRounding'] {
        return this.itemForm.get('countButtonRounding') as MiniCartSettingsFormType['countButtonRounding'];
    }

    public setItemColor({ value }: ColorPickerEventPayload, formElement: FormControl): void {
        formElement.patchValue(value);
    }

    public setModalBorderRadius(value: number): void {
        this.modalBorderRadius.setValue(value);
    }

    public setCountButtonRounding(value: number): void {
        this.countButtonRounding.setValue(value);
    }
}
